import { useState, useEffect } from 'react';
import CanonicalTag from '../components/CanonicalTag';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://api.estatecoin.in';

function fmt(n) {
  if (!n) return '₹0';
  if (n >= 10000000) return '₹' + (n/10000000).toFixed(2) + ' Cr';
  if (n >= 100000) return '₹' + (n/100000).toFixed(1) + 'L';
  if (n >= 1000) return '₹' + (n/1000).toFixed(1) + 'K';
  return '₹' + Number(n).toFixed(2);
}

const STATUS_COLORS = {
  active:     { bg: '#F0F7F3', color: '#1B3A2D', label: 'Active' },
  sell_voted: { bg: '#FEF3C7', color: '#92400E', label: 'Voted to Sell' },
  converted:  { bg: '#EDE9FE', color: '#4C1D95', label: 'Converted' },
  withdrawn:  { bg: '#F3F4F6', color: '#6B7280', label: 'Withdrawn' },
};

export default function MyCommunityPage() {
  const { user } = useAuth();
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState({});
  const [converting, setConverting] = useState({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    document.title = 'My Community Investments — EstateCoin';
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/community-properties/my-interests`, { credentials: 'include' });
      const d = await r.json();
      setInterests(d.interests || []);
    } catch(e) {}
    setLoading(false);
  };

  const handleVote = async (interestId, vote) => {
    setVoting(v => ({ ...v, [interestId]: true }));
    try {
      const r = await fetch(`${API}/api/community-properties/vote-sell`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interestId, vote }),
      });
      const d = await r.json();
      setMsg(d.message);
      load();
    } catch(e) {}
    setVoting(v => ({ ...v, [interestId]: false }));
  };

  const handleConvert = async (interestId) => {
    setConverting(v => ({ ...v, [interestId]: true }));
    try {
      const r = await fetch(`${API}/api/community-properties/convert`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interestId }),
      });
      const d = await r.json();
      setMsg(d.message || d.error);
      load();
    } catch(e) {}
    setConverting(v => ({ ...v, [interestId]: false }));
  };

  const S = { fontFamily: "'DM Sans', sans-serif" };

  if (!user) return (
    <div style={{ ...S, textAlign: 'center', padding: '80px 20px' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Sign in to view your community investments</div>
      <Link to="/login" style={{ color: '#1B3A2D', fontWeight: 600 }}>Sign In →</Link>
    </div>
  );

  return (
    <div style={{ ...S, minHeight: '100vh', background: '#F8F5F0' }}>
      <CanonicalTag/>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0D0D0D, #1B3A2D)', padding: '48px 24px 36px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>
            <Link to="/dashboard" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Dashboard</Link>
            {' › '}
            <Link to="/community" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Community</Link>
            {' › '} My Investments
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px,4vw,36px)', fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>
            My Community Investments
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            Track your pooled investments, rental income, and conversion status.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 20px' }}>

        {msg && (
          <div style={{ background: '#F0F7F3', border: '1px solid #C8E6D4', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#1B3A2D', fontWeight: 500 }}>
            ✅ {msg}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>Loading your investments...</div>
        )}

        {!loading && interests.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '1px solid #EDE4DC' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#111', marginBottom: 8 }}>No community investments yet</div>
            <p style={{ fontSize: 14, color: '#888', marginBottom: 24, lineHeight: 1.7 }}>
              Pool funds with other investors to own premium properties together.<br />Your money earns rental income while the pool fills.
            </p>
            <Link to="/community" style={{ display: 'inline-block', padding: '12px 28px', background: '#1B3A2D', color: '#fff', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Explore Community Properties →
            </Link>
          </div>
        )}

        {/* Interest Cards */}
        {interests.map(i => {
          const statusStyle = STATUS_COLORS[i.status] || STATUS_COLORS.active;
          const prop = i.property || {};
          const targetAmount = prop.targetAmount || 7000000;
          const poolPct = parseFloat(i.poolPct || 0);

          return (
            <div key={i._id} style={{ background: '#fff', borderRadius: 20, border: '1px solid #EDE4DC', overflow: 'hidden', marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

              {/* Card header */}
              <div style={{ background: 'linear-gradient(135deg, #1B3A2D, #264D38)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Community Property</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
                    {prop.name || i.propertyId}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>📍 {prop.location || 'Andheri East, Mumbai'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ padding: '4px 12px', borderRadius: 100, fontSize: 11, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color }}>
                    {statusStyle.label}
                  </span>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
                    Committed {new Date(i.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid #F0EDE8' }}>
                {[
                  { label: 'Committed', value: fmt(i.amount) },
                  { label: 'Parked in', value: i.parkedPropertyName || '—' },
                  { label: 'Tokens held', value: i.parkedTokens ? i.parkedTokens.toFixed(4) : '—' },
                  { label: 'Rental earned', value: fmt(i.rentalEarned) },
                ].map((s, idx) => (
                  <div key={idx} style={{ padding: '14px 16px', textAlign: 'center', borderRight: idx < 3 ? '1px solid #F0EDE8' : 'none', background: '#FAFAF8' }}>
                    <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1B3A2D', wordBreak: 'break-word' }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Pool progress */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #F0EDE8' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: '#555', fontWeight: 500 }}>Pool progress</span>
                  <span style={{ color: '#1B3A2D', fontWeight: 700 }}>{fmt(i.poolTotal)} of {fmt(targetAmount)} · {poolPct}%</span>
                </div>
                <div style={{ height: 8, background: '#F0EDE8', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.max(poolPct, 0.3)}%`, background: 'linear-gradient(90deg, #1B3A2D, #2D7A50)', borderRadius: 999, transition: 'width 1s' }} />
                </div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 5 }}>
                  {i.investorCount || i.totalVoters || 0} investors in pool · Your share: {targetAmount > 0 ? ((i.amount / targetAmount) * 100).toFixed(3) : 0}%
                </div>
              </div>

              {/* Actions */}
              <div style={{ padding: '20px 24px' }}>

                {/* Conversion available */}
                {prop.status === 'acquired' && i.conversionStatus === 'pending' && (
                  <div style={{ background: 'linear-gradient(135deg, #F0F7F3, #E8F4EE)', border: '1px solid #C8E6D4', borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#1B3A2D', marginBottom: 6, fontSize: 14 }}>🎉 Property Acquired! Choose your option:</div>
                    <div style={{ fontSize: 13, color: '#2D5C45', marginBottom: 16, lineHeight: 1.6 }}>
                      Your ₹{i.amount?.toLocaleString('en-IN')} worth of {i.parkedPropertyName} tokens can now be converted to {prop.name} tokens at the current price. Or keep your existing tokens.
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={() => handleConvert(i._id)} disabled={converting[i._id]}
                        style={{ flex: 1, padding: '12px', background: '#1B3A2D', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {converting[i._id] ? 'Converting...' : `Convert to ${prop.name} →`}
                      </button>
                      <button onClick={() => { setMsg('Keeping existing tokens. No action needed.'); }}
                        style={{ flex: 1, padding: '12px', background: '#F8F5F0', color: '#555', border: '1px solid #EDE4DC', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Keep my {i.parkedPropertyName} tokens
                      </button>
                    </div>
                  </div>
                )}

                {/* Advisory sell vote */}
                {i.status !== 'converted' && i.status !== 'withdrawn' && (
                  <div style={{ background: '#FAFAF8', border: '1px solid #EDE4DC', borderRadius: 14, padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#111', fontSize: 13, marginBottom: 4 }}>
                          Community sell vote (advisory)
                        </div>
                        <div style={{ fontSize: 12, color: '#888', lineHeight: 1.5 }}>
                          {i.sellVotes || 0} of {i.totalVoters || 0} investors ({i.sellVotePct || 0}%) want to sell.
                          Final decision is made by EstateCoin team.
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleVote(i._id, !i.voteToSell)} disabled={voting[i._id]}
                          style={{ padding: '8px 16px', borderRadius: 10, border: `1.5px solid ${i.voteToSell ? '#dc2626' : '#1B3A2D'}`, background: i.voteToSell ? '#FEE2E2' : '#F0F7F3', color: i.voteToSell ? '#dc2626' : '#1B3A2D', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {voting[i._id] ? '...' : i.voteToSell ? '↩ Withdraw vote' : '🏷 Vote to sell'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {i.status === 'converted' && (
                  <div style={{ textAlign: 'center', padding: '12px', background: '#EDE9FE', borderRadius: 12, fontSize: 13, color: '#4C1D95', fontWeight: 600 }}>
                    ✅ Converted to {prop.name} tokens
                  </div>
                )}
              </div>

              {/* Ledger entries link */}
              <div style={{ padding: '12px 24px', borderTop: '1px solid #F0EDE8', background: '#FAFAF8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#aaa' }}>
                  Lot ID: {i.lotId ? i.lotId.slice(-8) : '—'} · Parked @ ₹{i.parkedTokenPrice}/token
                </span>
                <Link to="/rental-income" style={{ fontSize: 12, color: '#1B3A2D', fontWeight: 600, textDecoration: 'none' }}>
                  View rental income →
                </Link>
              </div>
            </div>
          );
        })}

        {/* CTA to commit more */}
        {!loading && interests.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to="/community" style={{ display: 'inline-block', padding: '12px 28px', background: '#1B3A2D', color: '#fff', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              + Commit More →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
